import React, { useEffect, useState } from 'react';
import { useLayoutContext } from '@context/LayoutContext';
import { AvTooltip } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { APPBAR_HEIGHT, dotStyle, flex } from '@fabric-ui/theme';
import { envVariables } from '@fabric-ui/utils';
import { Box, Divider, Drawer, Fade, List, ListItem, Skeleton, useTheme } from '@mui/material';
import { useAvContext } from '../../context/AvContextProvider';
import { PathFeatureFlag, PUBLIC_APPS } from '../../types';
import { isDebugMode } from '../../utils/rum.utils';
import { useMdrSubscription } from './hooks';
import { appsSideBar, appsSideBarFooter, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_OPEN } from './sidebar.constants';
import SideBarItem, { item } from './SideBarItem';
import { OpenedSubMenus } from './types';

interface SectionTitleProps {
  isOpen: boolean;
  loading: boolean;
  name: string;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ isOpen, loading, name }) => (
  <ListItem sx={{ ...styles.sectionTitle, ...(!isOpen && { px: 2, minHeight: 48 }) }}>
    {loading ? (
      <Skeleton variant="text" height={isOpen ? 20 : 5} width={isOpen ? '40%' : '100%'} />
    ) : isOpen ? (
      name
    ) : (
      <Fade in timeout={800}>
        <Divider />
      </Fade>
    )}
  </ListItem>
);

const AvSidebar = () => {
  const {
    layoutWidths: { sideBar: sidebarWidth },
    setLayoutWidths,
  } = useLayoutContext();
  const {
    featureFlags,
    userPermissions: { hasAllowedPermission, isBackOfficeAvailable },
    isLoading,
    selectedApp,
    enabledApps,
  } = useAvContext();
  const [isOpen, setIsOpen] = useState(sidebarWidth === SIDEBAR_WIDTH_OPEN);
  const [isHovered, setIsHovered] = useState(false);
  const [openedSubMenus, setOpenedSubMenus] = useState<OpenedSubMenus[]>([]);

  useEffect(() => {
    setOpenedSubMenus(prev => prev.filter(v => v.app === selectedApp));
  }, [selectedApp]);

  const [shouldDisableHover, setShouldDisableHover] = useState(false);

  useEffect(() => setLayoutWidths('sideBar', isOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED), [isOpen]);

  const footer = appsSideBarFooter[selectedApp];
  const { hasMdrSubscription } = useMdrSubscription({ enabled: !!footer?.requiresMdrSubscription });
  const passesFlag = !footer?.ffForDisplaying || !!featureFlags[footer.ffForDisplaying];
  const passesSubscription = !!footer?.requiresMdrSubscription && hasMdrSubscription;
  const showFooter = !!footer && (passesFlag || passesSubscription) && (isOpen || isHovered); // Show the footer if one exists for this app AND either its feature flag is satisfied or the account has an MDR subscription AND the sidebar is open or hovered.

  return (
    <Drawer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={theme => styles.drawer(theme, sidebarWidth, isOpen, shouldDisableHover)}
      anchor="left"
      role="navigation"
      aria-label="sidebar navigation"
      variant="permanent">
      <SideBarToggler open={isOpen} toggle={() => setIsOpen(!isOpen)} setShouldDisableHover={setShouldDisableHover} />
      {(!!enabledApps.some(({ id }) => id === selectedApp) || PUBLIC_APPS.includes(selectedApp)) &&
        appsSideBar[selectedApp]?.map(({ name, subMenu }) => {
          const hasChild = subMenu.some(
            ({ path, permission }) =>
              hasAllowedPermission({ path, app: selectedApp, permission }) &&
              (!PathFeatureFlag[path] || featureFlags[PathFeatureFlag[path]])
          );
          const expandDrawer = isOpen || isHovered;
          return (
            <React.Fragment key={name}>
              {(isLoading || hasChild) && <SectionTitle isOpen={expandDrawer} loading={isLoading} name={name} />}
              {isLoading ? (
                <List>
                  {['70%', '60%'].map(percentage => (
                    <ListItem key={percentage} sx={theme => styles.item(theme)}>
                      <Skeleton variant="text" height={isOpen ? 20 : 30} width={isOpen ? percentage : '100%'} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                hasChild && (
                  <List sx={{ ...flex.col, gap: 1 }}>
                    {subMenu
                      .filter(item => !item.isBackOffice || isBackOfficeAvailable)
                      .map(subMenuItem => (
                        <SideBarItem
                          isOpen={expandDrawer}
                          {...subMenuItem}
                          key={`${subMenuItem.name}_${selectedApp}_${subMenuItem.path}${subMenuItem.landingPage ? '_landingPage' : ''}`}
                          {...{ openedSubMenus, setOpenedSubMenus }}
                        />
                      ))}
                  </List>
                )
              )}
            </React.Fragment>
          );
        })}
      {((isDebugMode() && isOpen) || showFooter) && <span style={{ flexGrow: 1 }} />}
      {isDebugMode() && isOpen && (
        <List>
          <ListItem sx={styles.sectionTitle}>
            <Divider />
          </ListItem>
          <ListItem sx={styles.sectionTitle}>
            <AvTooltip>
              <>Version: {envVariables.VITE_VERSION}</>
            </AvTooltip>
          </ListItem>
        </List>
      )}
      {showFooter && (
        <List>
          <ListItem sx={{ ...styles.sectionTitle, height: 'auto', marginTop: 0, paddingBottom: 0 }}>{footer.title}</ListItem>
          <ListItem sx={{ color: 'var(--color-content-base-primary)', padding: '2px 24px 0', height: 'auto' }}>{footer.value}</ListItem>
        </List>
      )}
    </Drawer>
  );
};
export default AvSidebar;

const styles = {
  drawer: (theme, sidebarWidth, isOpen, shouldDisableHover) => ({
    zIndex: theme.zIndex.drawer + 5,
    gridArea: 'sidebar',
    overflowY: 'auto',
    transition: theme.transitions.create(['width']),
    ...(shouldDisableHover && { pointerEvents: 'none' }),
    '.MuiDrawer-paper': {
      borderColor: 'var(--color-border-base-primary)',
      ':hover': {
        borderColor: 'var(--color-content-interactive-primary-default)',
        ...(!isOpen && {
          width: SIDEBAR_WIDTH_OPEN,
        }),
      },
      width: sidebarWidth,
      backgroundColor: 'var(--color-surface-nav-sideBar-default)',
      color: 'var(--color-content-base-tertiary)',
      position: 'unset',
      padding: '16px 0',
      transition: `${theme.transitions.create('width')}, ${theme.transitions.create('border-color', { duration: theme.transitions.duration.shortest })}`,
      whiteSpace: 'nowrap',
      overflowX: 'clip',
      '.av-sidebar-toggler': {
        opacity: 0,
        transition: theme.transitions.create(['left', 'opacity', 'background-color']),
      },
      '&:hover': {
        '.av-sidebar-toggler': {
          opacity: 1,
          border: '1px solid var(--color-content-interactive-primary-default)',
          backgroundColor: 'var(--color-surface-nav-sideBar-hover)',
          left: SIDEBAR_WIDTH_OPEN,
        },
      },
    },
  }),
  sectionTitle: {
    color: 'var(--color-content-base-tertiary)',
    height: 48,
    padding: '16px 24px',
    letterSpacing: '0.7px',
    marginTop: '2px',
    fontWeight: 600,
    fontSize: 12,
    opacity: 0.7,
    '& hr': {
      backgroundColor: 'var(--color-border-base-primary)',
      height: '1px',
      border: 'none',
      borderRadius: '12px',
      width: '100%',
    },
    '& + ul': {
      padding: 0,
    },
  },
  item,
};

interface SideBarTogglerProps {
  open: boolean;
  toggle: () => void;
  setShouldDisableHover: React.Dispatch<React.SetStateAction<boolean>>;
}
const SideBarToggler: React.FC<SideBarTogglerProps> = ({ open, toggle, setShouldDisableHover }) => {
  const {
    layoutWidths: { sideBar: sidebarWidth = SIDEBAR_WIDTH_OPEN },
  } = useLayoutContext();
  const theme = useTheme();
  const dotSx = dotStyle(() => 'var(--color-surface-nav-sideBar-hover)', 24);
  const sideBarTogglerStyle = {
    ...flex.center,
    ...dotSx,
    position: 'fixed',
    top: 30 + APPBAR_HEIGHT,
    left: sidebarWidth,
    transform: `translateX(-50%) rotate(${open ? '' : '-'}90deg)`,
    cursor: 'pointer',
    zIndex: theme => theme.zIndex.modal,
    backdropFilter: 'blur(15.5676px)',
    ':hover': {
      background: 'var(--color-surface-nav-sideBar-active)',
    },
    svg: {
      width: 16,
      height: 16,
      color: 'var(--color-content-base-secondary)',
    },
  };

  return (
    <Box
      className="av-sidebar-toggler"
      sx={sideBarTogglerStyle}
      onClick={() => {
        toggle();
        if (open) {
          setShouldDisableHover(true);
          setTimeout(() => setShouldDisableHover(false), theme.transitions.duration.standard);
        }
      }}>
      <ArrowDown />
    </Box>
  );
};
